/**
 * Data Portability Redux Slice
 *
 * Manages export and import state for user data portability.
 * Supports scoped export (transactions, budgets, goals, accounts, categories)
 * and selective import with conflict resolution strategies.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type {
  DataScope,
  ConflictStrategy,
  ExportFile,
  ImportValidation,
  ImportResult,
} from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

// ─── State ───────────────────────────────────────────────────────────────────

interface DataPortabilityState {
  // Export
  exportLoading: boolean;
  exportError: string | null;

  // Validate import
  validation: ImportValidation | null;
  validateLoading: boolean;
  validateError: string | null;

  // Import
  importResult: ImportResult | null;
  importLoading: boolean;
  importError: string | null;
}

const initialState: DataPortabilityState = {
  exportLoading: false,
  exportError: null,
  validation: null,
  validateLoading: false,
  validateError: null,
  importResult: null,
  importLoading: false,
  importError: null,
};

// ─── Thunks ──────────────────────────────────────────────────────────────────

/**
 * Export user data for selected scopes as a downloadable ExportFile.
 */
export const exportData = createAsyncThunk(
  'dataPortability/export',
  async (
    params: { scopes: DataScope[]; dateFrom?: string; dateTo?: string },
    { rejectWithValue }
  ) => {
    try {
      const queryParams: Record<string, string> = {
        scopes: params.scopes.join(','),
      };
      if (params.dateFrom) queryParams.dateFrom = params.dateFrom;
      if (params.dateTo) queryParams.dateTo = params.dateTo;

      const response = await api.get<{ success: boolean; data: ExportFile }>(
        endpoints.data.export,
        { params: queryParams }
      );
      return response.data.data as ExportFile;
    } catch (error: unknown) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to export data')
      );
    }
  }
);

/**
 * Validate an export file before importing (dry-run).
 * Backend expects the raw ExportFile as the POST body.
 */
export const validateImport = createAsyncThunk(
  'dataPortability/validateImport',
  async (
    params: { file: ExportFile },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<{ success: boolean; data: ImportValidation }>(
        endpoints.data.validateImport,
        params.file // send raw export file as body
      );
      return response.data.data as ImportValidation;
    } catch (error: unknown) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to validate import file')
      );
    }
  }
);

/**
 * Execute the import from an export file.
 * Backend expects { file: ExportFile, options: { scopes, conflictStrategy } }
 */
export const importData = createAsyncThunk(
  'dataPortability/import',
  async (
    params: { file: ExportFile; scopes: DataScope[]; conflictStrategy: ConflictStrategy },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<{ success: boolean; data: ImportResult }>(
        endpoints.data.import,
        {
          file: params.file,
          options: {
            scopes: params.scopes,
            conflictStrategy: params.conflictStrategy,
          },
        }
      );
      return response.data.data as ImportResult;
    } catch (error: unknown) {
      return rejectWithValue(
        getApiErrorMessage(error, 'Failed to import data')
      );
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const dataPortabilitySlice = createSlice({
  name: 'dataPortability',
  initialState,
  reducers: {
    clearValidation(state) {
      state.validation = null;
      state.validateError = null;
    },
    clearImportResult(state) {
      state.importResult = null;
      state.importError = null;
    },
    clearExportError(state) {
      state.exportError = null;
    },
  },
  extraReducers: (builder) => {
    // Export
    builder
      .addCase(exportData.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportData.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload as string;
      });

    // Validate import
    builder
      .addCase(validateImport.pending, (state) => {
        state.validateLoading = true;
        state.validateError = null;
        state.validation = null;
      })
      .addCase(validateImport.fulfilled, (state, action) => {
        state.validateLoading = false;
        state.validation = action.payload;
      })
      .addCase(validateImport.rejected, (state, action) => {
        state.validateLoading = false;
        state.validateError = action.payload as string;
      });

    // Import
    builder
      .addCase(importData.pending, (state) => {
        state.importLoading = true;
        state.importError = null;
        state.importResult = null;
      })
      .addCase(importData.fulfilled, (state, action) => {
        state.importLoading = false;
        state.importResult = action.payload;
      })
      .addCase(importData.rejected, (state, action) => {
        state.importLoading = false;
        state.importError = action.payload as string;
      });
  },
});

export const { clearValidation, clearImportResult, clearExportError } =
  dataPortabilitySlice.actions;
export default dataPortabilitySlice.reducer;
